/**
 * Cancellable static/TTS audio for live Speaking exams.
 *
 * Explicit Quit must not start or keep playing ending/static/realtime clips.
 * Normal completion uses the same helper without markLiveExamRuntimeExiting().
 */

import {
  getLiveExamAudioEpoch,
  invokeUnlessLiveExamExiting,
  isLiveExamRuntimeExiting,
  shouldStartLiveExamAudio,
  trackLiveExamBufferSource,
  trackLiveExamHtmlAudio,
  untrackLiveExamBufferSource,
  untrackLiveExamHtmlAudio,
} from './liveExamRuntimeTeardown';

export type PlayTrackedLiveExamAudioResult = 'ended' | 'aborted' | 'failed';

export type PlayTrackedLiveExamAudioOptions = {
  url: string;
  audioContext?: AudioContext | null;
  onEnd?: () => void;
  /** Fires once audible output is scheduled (WebAudio `start` or HTMLAudio `play` success). */
  onPlaybackStarted?: () => void;
};

function resolveAudioContext(explicit?: AudioContext | null): AudioContext | undefined {
  if (explicit && explicit.state !== 'closed') return explicit;
  try {
    const ctx = (window as Window & { __appAudioContext?: AudioContext }).__appAudioContext;
    if (ctx && ctx.state !== 'closed') return ctx;
  } catch {
    /* noop */
  }
  return undefined;
}

async function decodeAudioBuffer(ctx: AudioContext, arr: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise<AudioBuffer>((resolve, reject) => {
    ctx.decodeAudioData(arr, resolve, reject);
  });
}

/**
 * Play a URL via WebAudio when possible, otherwise detached HTMLAudio.
 * Tracks the playing object so explicit quit can stop it.
 * Refuses to start (and suppresses onEnd) when the runtime is exiting.
 */
export function playTrackedLiveExamAudio(
  options: PlayTrackedLiveExamAudioOptions,
): Promise<PlayTrackedLiveExamAudioResult> {
  const { url, onEnd, onPlaybackStarted } = options;
  const epoch = getLiveExamAudioEpoch();

  const isStale = () =>
    isLiveExamRuntimeExiting() || epoch !== getLiveExamAudioEpoch();

  const finishEnd = () => invokeUnlessLiveExamExiting(onEnd, epoch);

  return new Promise(async (resolve) => {
    if (!shouldStartLiveExamAudio() || isStale()) {
      resolve('aborted');
      return;
    }

    const ctx = resolveAudioContext(options.audioContext);
    if (ctx) {
      try {
        try {
          await ctx.resume();
        } catch {
          /* noop */
        }
        if (isStale()) {
          resolve('aborted');
          return;
        }
        if (ctx.state === 'running') {
          const resp = await fetch(url, { mode: 'cors' });
          if (isStale()) {
            resolve('aborted');
            return;
          }
          const arr = await resp.arrayBuffer();
          if (isStale()) {
            resolve('aborted');
            return;
          }
          const buffer = await decodeAudioBuffer(ctx, arr);
          if (isStale() || ctx.state === 'closed') {
            resolve('aborted');
            return;
          }
          const src = ctx.createBufferSource();
          src.buffer = buffer;
          src.connect(ctx.destination);
          src.onended = () => {
            untrackLiveExamBufferSource(src);
            if (isStale()) {
              resolve('aborted');
              return;
            }
            finishEnd();
            resolve('ended');
          };
          if (!trackLiveExamBufferSource(src)) {
            resolve('aborted');
            return;
          }
          src.start(0);
          if (!isStale()) {
            onPlaybackStarted?.();
          }
          return;
        }
      } catch {
        if (isStale()) {
          resolve('aborted');
          return;
        }
        // Fall through to HTMLAudio only when we are still in an active exam.
      }
    }

    if (isStale()) {
      resolve('aborted');
      return;
    }

    try {
      const audio = new Audio(url);
      try {
        (audio as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
        audio.setAttribute('playsinline', 'true');
      } catch {
        /* noop */
      }

      audio.onended = () => {
        untrackLiveExamHtmlAudio(audio);
        if (isStale()) {
          resolve('aborted');
          return;
        }
        finishEnd();
        resolve('ended');
      };
      audio.onerror = () => {
        untrackLiveExamHtmlAudio(audio);
        if (isStale()) {
          resolve('aborted');
          return;
        }
        resolve('failed');
      };

      if (!trackLiveExamHtmlAudio(audio)) {
        resolve('aborted');
        return;
      }

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        try {
          await playPromise;
          if (!isStale()) {
            onPlaybackStarted?.();
          }
        } catch {
          untrackLiveExamHtmlAudio(audio);
          try {
            audio.pause();
          } catch {
            /* noop */
          }
          if (isStale()) {
            resolve('aborted');
            return;
          }
          resolve('failed');
        }
      } else if (!isStale()) {
        onPlaybackStarted?.();
      }
    } catch {
      if (isStale()) {
        resolve('aborted');
        return;
      }
      resolve('failed');
    }
  });
}
